import React, { useEffect, useState, useMemo } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import {
  ArrowRight,
  Phone,
  Mail,
  MapPin,
  Check,
  ChevronDown,
  ShieldCheck,
  CircleDollarSign,
  Truck,
  Landmark,
  Home,
  FileText,
  Briefcase,
  Lock,
} from "lucide-react";
import CFLogo from "@/components/CFLogo";
import ChatWidget from "@/components/ChatWidget";
import { useAuth } from "@/context/AuthContext";
import { extractAxiosError } from "@/lib/api-errors";
import { PRODUCTS, PRODUCT_LIST } from "@/data/products";
import { APPLY_LINK_PROPS } from "@/constants/links";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ICONS = {
  CircleDollarSign,
  Truck,
  Landmark,
  Home,
  FileText,
  Briefcase,
};

// ---------------------------------------------------------------------------
// SEO + structured data
// ---------------------------------------------------------------------------
const useSeo = (product) => {
  useEffect(() => {
    if (!product) return;
    document.title = product.seoTitle;
    let meta = document.querySelector('meta[name="description"]');
    if (!meta) {
      meta = document.createElement("meta");
      meta.setAttribute("name", "description");
      document.head.appendChild(meta);
    }
    meta.setAttribute("content", product.seoDescription);

    // JSON-LD FinancialProduct schema for rich search results
    const ldId = "ld-financial-product";
    let ld = document.getElementById(ldId);
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = ldId;
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FinancialProduct",
      name: product.homeTitle,
      description: product.seoDescription,
      provider: {
        "@type": "FinancialService",
        name: "Capless Funding",
        url: "https://caplessfunding.com",
        telephone: "+1-737-775-7077",
      },
      url: `https://caplessfunding.com/funding/${product.slug}`,
    });

    return () => {
      // Reset title on unmount
      document.title = "Capless Funding — Direct Lender for Growing Businesses";
    };
  }, [product]);
};

// ---------------------------------------------------------------------------
// Header (light)
// ---------------------------------------------------------------------------
const Header = () => (
  <header className="sticky top-0 z-50 backdrop-blur-md bg-cf-cream/85 border-b border-cf-line/60">
    <div className="max-w-7xl mx-auto px-6 md:px-10 h-20 flex items-center justify-between">
      <CFLogo testId="product-header-logo" />
      <nav className="hidden lg:flex items-center gap-8 text-sm text-cf-ink-soft">
        <Link to="/#products" className="cf-link-underline hover:text-cf-ink transition">All Funding</Link>
        <Link to="/#about" className="cf-link-underline hover:text-cf-ink transition">About</Link>
        <Link to="/#process" className="cf-link-underline hover:text-cf-ink transition">How it works</Link>
        <a href="tel:+17377757077" className="cf-link-underline hover:text-cf-ink transition flex items-center gap-1.5">
          <Phone size={13} strokeWidth={1.8} /> (737) 775-7077
        </a>
      </nav>
      <div className="flex items-center gap-2 md:gap-3">
        <Link to="/merchant/login" className="hidden sm:inline-block text-xs tracking-[0.16em] uppercase text-cf-ink-soft hover:text-cf-ink transition cf-link-underline">
          Merchant portal
        </Link>
        <a {...APPLY_LINK_PROPS} data-testid="product-nav-apply-btn" className="cf-btn-primary text-sm">
          Apply now <ArrowRight size={15} strokeWidth={2} />
        </a>
      </div>
    </div>
  </header>
);

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------
const Hero = ({ product }) => {
  const Icon = ICONS[product.icon] || CircleDollarSign;
  return (
    <section className="relative cf-soft-rays overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-16 md:pb-20">
        <div className="text-sm text-cf-ink-soft mb-6 flex items-center gap-2">
          <Link to="/" className="hover:text-cf-ink cf-link-underline">Home</Link>
          <span className="text-cf-line">/</span>
          <Link to="/#products" className="hover:text-cf-ink cf-link-underline">Funding</Link>
          <span className="text-cf-line">/</span>
          <span className="text-cf-ink">{product.homeTitle}</span>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="inline-flex items-center gap-3 px-3 py-1.5 bg-white border border-cf-line rounded-full"
            >
              <div className="w-7 h-7 rounded-full bg-cf-mint flex items-center justify-center">
                <Icon size={14} strokeWidth={1.8} className="text-cf-ink" />
              </div>
              <span className="text-xs uppercase tracking-[0.18em] text-cf-ink-soft font-semibold">
                {product.heroEyebrow}
              </span>
            </motion.div>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.1 }}
              className="font-serif text-cf-ink mt-6 tracking-tight leading-[1.05]"
              style={{ fontSize: "clamp(2.1rem, 4.6vw, 3.8rem)", fontWeight: 500 }}
            >
              {product.heroHeadline}
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="mt-6 max-w-2xl text-base md:text-lg text-cf-ink-soft leading-relaxed"
            >
              {product.heroSubhead}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.3 }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <a {...APPLY_LINK_PROPS} data-testid="product-hero-apply-btn" className="cf-btn-gold">
                Apply now <ArrowRight size={16} strokeWidth={2} />
              </a>
              <a href="tel:+17377757077" className="cf-btn-ghost-light">
                <Phone size={15} strokeWidth={1.8} /> Talk to an advisor
              </a>
            </motion.div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-white rounded-3xl border border-cf-line p-6 md:p-8 shadow-[0_30px_70px_-30px_rgba(15,37,64,0.2)]">
              <div className="cf-overline mb-4">At a glance</div>
              <ul className="space-y-4">
                {product.keyFacts.map((f) => (
                  <li key={f.label} className="flex items-start justify-between gap-4">
                    <span className="text-cf-ink-soft text-sm">{f.label}</span>
                    <span className="text-cf-ink font-semibold text-right">{f.value}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Use cases
// ---------------------------------------------------------------------------
const UseCases = ({ product }) => (
  <section className="py-16 md:py-20 bg-cf-paper border-y border-cf-line">
    <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
      <div className="lg:col-span-5">
        <span className="cf-overline">Who it&apos;s for</span>
        <h2 className="font-serif text-3xl md:text-4xl text-cf-ink mt-4 leading-tight tracking-tight">
          Built for businesses that need <span className="italic text-cf-gold">to move now</span>.
        </h2>
      </div>
      <div className="lg:col-span-7">
        <ul className="grid sm:grid-cols-2 gap-4">
          {product.useCases.map((u) => (
            <li key={u} className="flex items-start gap-3">
              <div className="w-6 h-6 rounded-full bg-cf-mint flex items-center justify-center shrink-0 mt-0.5">
                <Check size={14} strokeWidth={2.2} className="text-cf-ink" />
              </div>
              <span className="text-cf-ink-soft text-[0.95rem]">{u}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Why Capless
// ---------------------------------------------------------------------------
const WhyCapless = ({ product }) => (
  <section className="py-16 md:py-24">
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <div className="max-w-2xl">
        <span className="cf-overline">Why Capless</span>
        <h2 className="font-serif text-3xl md:text-5xl text-cf-ink mt-4 leading-tight tracking-tight">
          The Capless difference, in three lines.
        </h2>
      </div>
      <div className="grid md:grid-cols-3 gap-6 mt-12">
        {product.why.map((w) => (
          <div key={w.title} className="cf-card-light p-7">
            <h3 className="font-serif text-xl md:text-2xl text-cf-ink tracking-tight">{w.title}</h3>
            <p className="text-cf-slate text-[0.95rem] leading-relaxed mt-3">{w.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Requirements
// ---------------------------------------------------------------------------
const Requirements = ({ product }) => (
  <section className="py-16 md:py-20 bg-cf-paper border-y border-cf-line">
    <div className="max-w-5xl mx-auto px-6 md:px-10">
      <div className="text-center max-w-2xl mx-auto">
        <span className="cf-overline">Minimum requirements</span>
        <h2 className="font-serif text-3xl md:text-4xl text-cf-ink mt-4 leading-tight tracking-tight">
          Are we a match?
        </h2>
        <p className="text-cf-ink-soft text-sm mt-4">
          Don&apos;t quite meet every line? Reach out anyway — we have programs for newer businesses, too.
        </p>
      </div>
      <ul className="grid sm:grid-cols-2 gap-4 mt-10">
        {product.requirements.map((r) => (
          <li key={r} className="flex items-start gap-3 bg-white border border-cf-line rounded-2xl p-5">
            <ShieldCheck size={18} strokeWidth={1.8} className="text-cf-gold shrink-0 mt-0.5" />
            <span className="text-cf-ink text-[0.95rem]">{r}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// FAQ
// ---------------------------------------------------------------------------
const Faq = ({ product }) => {
  const [open, setOpen] = useState(0);
  return (
    <section className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-4">
          <span className="cf-overline">FAQ</span>
          <h2 className="font-serif text-3xl md:text-5xl text-cf-ink mt-4 leading-tight tracking-tight">
            Common questions about <span className="italic text-cf-gold">{product.homeTitle}</span>.
          </h2>
          <p className="text-cf-ink-soft text-base mt-5">
            More questions? Call <a className="text-cf-ink font-semibold cf-link-underline" href="tel:+17377757077">(737) 775-7077</a>.
          </p>
        </div>
        <div className="lg:col-span-7 lg:col-start-6">
          {product.faqs.map((f, i) => (
            <div key={f.q} className="border-b border-cf-line">
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between py-6 text-left"
              >
                <span className="font-serif text-lg md:text-xl text-cf-ink tracking-tight pr-6">{f.q}</span>
                <div className="w-8 h-8 rounded-full bg-cf-mint flex items-center justify-center shrink-0">
                  <ChevronDown
                    size={16} strokeWidth={2}
                    className={`text-cf-ink transition-transform duration-300 ${open === i ? "rotate-180" : ""}`}
                  />
                </div>
              </button>
              {open === i && (
                <p className="text-cf-ink-soft text-[0.95rem] leading-relaxed pb-6 pr-12">{f.a}</p>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Apply form (pre-selects this product's program)
// ---------------------------------------------------------------------------
const Apply = ({ product }) => {
  const { refreshMe } = useAuth();
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    phone: "",
    funding_amount: "",
    product: product.applyProductValue,
    monthly_revenue: "",
    time_in_business: "",
    password: "",
    notes: "",
  });
  const [status, setStatus] = useState({ loading: false, ok: false, error: "" });
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      setStatus({ loading: false, ok: false, error: "Please choose a password of at least 6 characters." });
      return;
    }
    setStatus({ loading: true, ok: false, error: "" });
    try {
      await axios.post(`${API}/leads`, form, { withCredentials: true });
      setStatus({ loading: false, ok: true, error: "" });
      await refreshMe();
      setTimeout(() => { window.location.href = "/merchant/dashboard"; }, 900);
    } catch (err) {
      setStatus({ loading: false, ok: false, error: extractAxiosError(err, "Something went wrong. Please try again.") });
    }
  };

  return (
    <section id="apply" className="py-16 md:py-24 bg-cf-paper border-y border-cf-line">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto mb-10">
          <span className="cf-overline">Apply for {product.homeTitle}</span>
          <h2 className="font-serif text-3xl md:text-5xl text-cf-ink mt-4 leading-tight tracking-tight">
            Ready when <span className="italic text-cf-gold">you are</span>.
          </h2>
          <p className="text-cf-ink-soft text-base mt-5">
            Apply once and instantly get your own portal to track status, upload documents, and chat with your advisor.
          </p>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-3xl border border-cf-line p-8 md:p-10 shadow-[0_30px_70px_-30px_rgba(15,37,64,0.18)]"
        >
          <div className="grid sm:grid-cols-2 gap-5">
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Business name</label>
              <input required value={form.business_name} onChange={update("business_name")} className="cf-input-light" placeholder="Acme Logistics LLC" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Your name</label>
              <input required value={form.contact_name} onChange={update("contact_name")} className="cf-input-light" placeholder="Jordan Cole" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Email</label>
              <input required type="email" value={form.email} onChange={update("email")} className="cf-input-light" placeholder="jordan@acme.com" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Phone</label>
              <input required value={form.phone} onChange={update("phone")} className="cf-input-light" placeholder="(212) 555-0190" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Amount needed</label>
              <input required value={form.funding_amount} onChange={update("funding_amount")} className="cf-input-light" placeholder="$250,000" />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Funding program</label>
              <div className="relative">
                <select required value={form.product} onChange={update("product")} className="cf-input-light appearance-none pr-10 cursor-pointer">
                  {PRODUCT_LIST.map((p) => (
                    <option key={p.slug} value={p.applyProductValue}>{p.homeTitle}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-cf-ink-soft pointer-events-none" size={16} />
              </div>
            </div>
            <div>
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Monthly revenue</label>
              <input value={form.monthly_revenue} onChange={update("monthly_revenue")} className="cf-input-light" placeholder="$85,000" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Time in business</label>
              <input value={form.time_in_business} onChange={update("time_in_business")} className="cf-input-light" placeholder="3 years" />
            </div>
            <div className="sm:col-span-2 rounded-2xl bg-cf-mint/50 border border-cf-mint p-5">
              <div className="flex items-center gap-2 mb-3">
                <Lock size={15} strokeWidth={1.8} className="text-cf-ink" />
                <span className="text-sm font-semibold text-cf-ink">Create your merchant portal</span>
              </div>
              <p className="text-xs text-cf-slate mb-4">
                Choose a password to sign in to your portal and track your application.
              </p>
              <input
                required type="password" value={form.password} onChange={update("password")}
                className="cf-input-light" placeholder="Choose a password (min 6 chars)" autoComplete="new-password"
              />
            </div>
            <div className="sm:col-span-2">
              <label className="block text-xs font-semibold text-cf-ink-soft mb-2 uppercase tracking-wider">Anything else? (optional)</label>
              <textarea value={form.notes} onChange={update("notes")} rows={3} className="cf-input-light resize-none" placeholder="Anything that helps us help you faster." />
            </div>
          </div>

          {status.error && (
            <div className="mt-6 p-4 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">{status.error}</div>
          )}
          {status.ok && (
            <div className="mt-6 p-4 rounded-xl bg-cf-mint border border-cf-gold/30 text-cf-ink text-sm">
              ✓ Application submitted! Redirecting you to your portal…
            </div>
          )}

          <button type="submit" disabled={status.loading} className="cf-btn-gold w-full justify-center mt-7 disabled:opacity-50">
            {status.loading ? "Submitting..." : <>Submit & open my portal <ArrowRight size={16} strokeWidth={2} /></>}
          </button>
          <p className="text-cf-slate text-xs mt-3 text-center">
            Already applied? <Link to="/merchant/login" className="text-cf-ink font-semibold cf-link-underline">Sign in to your portal</Link>
          </p>
        </form>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Related products
// ---------------------------------------------------------------------------
const Related = ({ product }) => {
  const related = PRODUCT_LIST.filter((p) => p.slug !== product.slug).slice(0, 3);
  return (
    <section className="py-16 md:py-24">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <span className="cf-overline">Explore other programs</span>
          <h2 className="font-serif text-3xl md:text-4xl text-cf-ink mt-4 leading-tight tracking-tight">
            More ways Capless can fund your business.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6 mt-10">
          {related.map((p) => {
            const Icon = ICONS[p.icon] || CircleDollarSign;
            return (
              <Link
                key={p.slug}
                to={`/funding/${p.slug}`}
                className="cf-card-light p-6 md:p-7 block"
              >
                <div className="w-11 h-11 rounded-2xl bg-cf-mint flex items-center justify-center mb-4">
                  <Icon size={20} strokeWidth={1.7} className="text-cf-ink" />
                </div>
                <h3 className="font-serif text-xl text-cf-ink tracking-tight">{p.homeTitle}</h3>
                <p className="text-cf-slate text-sm leading-relaxed mt-2">{p.homeDesc}</p>
                <div className="mt-5 text-sm text-cf-ink font-semibold inline-flex items-center gap-1 cf-link-underline w-fit">
                  Learn more <ArrowRight size={14} strokeWidth={2} />
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Footer
// ---------------------------------------------------------------------------
const Footer = () => (
  <footer className="bg-cf-paper border-t border-cf-line">
    <div className="max-w-7xl mx-auto px-6 md:px-10 py-14 grid md:grid-cols-12 gap-10">
      <div className="md:col-span-6">
        <CFLogo testId="product-footer-logo" />
        <p className="text-cf-ink-soft text-sm leading-relaxed mt-5 max-w-md">
          Capless Funding is a direct lender helping small businesses access the capital they need — quickly, and on terms that fit.
        </p>
        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-cf-ink-soft text-sm">
          <div className="flex items-center gap-2"><Phone size={14} strokeWidth={1.6} className="text-cf-gold" /> (737) 775-7077</div>
          <div className="flex items-center gap-2"><Mail size={14} strokeWidth={1.6} className="text-cf-gold" /> info@caplessfunding.com</div>
          <div className="flex items-center gap-2"><MapPin size={14} strokeWidth={1.6} className="text-cf-gold" /> New York, NY</div>
        </div>
      </div>
      <div className="md:col-span-3">
        <div className="cf-overline mb-4">Funding</div>
        <ul className="space-y-2 text-cf-ink-soft text-sm">
          {PRODUCT_LIST.map((p) => (
            <li key={p.slug}><Link to={`/funding/${p.slug}`} className="hover:text-cf-ink transition">{p.homeTitle}</Link></li>
          ))}
        </ul>
      </div>
      <div className="md:col-span-3">
        <div className="cf-overline mb-4">Sign in</div>
        <ul className="space-y-2 text-cf-ink-soft text-sm">
          <li><Link to="/merchant/login" className="hover:text-cf-ink transition">Merchant portal</Link></li>
          <li><Link to="/agent/login" className="hover:text-cf-ink transition">Agent console</Link></li>
        </ul>
      </div>
    </div>
    <div className="border-t border-cf-line">
      <div className="max-w-7xl mx-auto px-6 md:px-10 py-5 text-xs text-cf-slate text-center">
        © {new Date().getFullYear()} Capless Funding. All rights reserved.
      </div>
    </div>
  </footer>
);

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------
const ProductPage = () => {
  const { slug } = useParams();
  const product = useMemo(() => PRODUCTS[slug], [slug]);
  useSeo(product);

  if (!product) return <Navigate to="/" replace />;

  return (
    <div className="bg-cf-cream text-cf-ink font-sans">
      <Header />
      <Hero product={product} />
      <UseCases product={product} />
      <WhyCapless product={product} />
      <Requirements product={product} />
      <Faq product={product} />
      <Apply product={product} />
      <Related product={product} />
      <Footer />
      <ChatWidget />
    </div>
  );
};

export default ProductPage;
